/**
 * FAVORITES / FAVORITENLISTE — JavaScript
 * ========================================
 * Herz-Button Toggle, Optimistic UI, Header-Counter
 * bergstadtatelier.de
 *
 * Endpoints (via Shopify App Proxy):
 *   GET  /apps/wishlist/status  → { success, favorites: ["123", ...], count }
 *   POST /apps/wishlist/toggle  → { success, action, product_id, count }
 */

(function () {
  'use strict';

  // Guard gegen doppeltes Laden (falls Script in mehreren Sections eingebunden)
  if (window.__favoritesLoaded) return;
  window.__favoritesLoaded = true;

  // ─── Config ───────────────────────────────────────────────────
  const TOGGLE_URL = '/apps/wishlist/toggle';
  const STATUS_URL = '/apps/wishlist/status';

  // ─── State ────────────────────────────────────────────────────
  // Set von Produkt-IDs die aktuell favorisiert sind
  let favoriteIds = new Set();
  let isLoggedIn = false;
  let isLoading = false;

  // ─── SVG Templates ────────────────────────────────────────────
  const HEART_EMPTY_SVG = `<svg class="fav-btn__icon fav-btn__icon--empty" aria-hidden="true" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <path fill-rule="evenodd" d="M10 5.24 8.515 3.773a4.433 4.433 0 0 0-6.21 0 4.293 4.293 0 0 0 0 6.128L10 17.495l7.695-7.593a4.293 4.293 0 0 0 0-6.128 4.433 4.433 0 0 0-6.21 0zm.765-2.177c2.113-2.084 5.538-2.084 7.65 0a5.29 5.29 0 0 1 0 7.55l-7.695 7.593a1.03 1.03 0 0 1-1.44 0l-7.696-7.594a5.29 5.29 0 0 1 0-7.549C3.697.98 7.122.98 9.234 3.063l.766.755z"/>
  </svg>`;

  const HEART_FILLED_SVG = `<svg class="fav-btn__icon fav-btn__icon--filled" aria-hidden="true" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 5.24 8.515 3.773a4.433 4.433 0 0 0-6.21 0 4.293 4.293 0 0 0 0 6.128L10 17.495l7.695-7.593a4.293 4.293 0 0 0 0-6.128 4.433 4.433 0 0 0-6.21 0z"/>
  </svg>`;

  // ─── Init: Favoriten-IDs laden ────────────────────────────────
  function init() {
    // Customer-ID aus dem Liquid-gerenderten Script-Block lesen
    const dataEl = document.getElementById('favorites-data');
    if (dataEl) {
      try {
        const data = JSON.parse(dataEl.textContent);
        isLoggedIn = data.logged_in || false;
        if (data.favorites && Array.isArray(data.favorites)) {
          data.favorites.forEach(id => favoriteIds.add(String(id)));
        }
      } catch (e) {
        console.warn('Favorites: Konnte initiale Daten nicht parsen', e);
      }
    }

    // Alle Herz-Buttons initialisieren
    updateAllButtons();
    updateHeaderCounter();

    // Event-Delegation fuer dynamisch erstellte Buttons
    document.addEventListener('click', handleFavClick);
  }

  // ─── Click Handler ────────────────────────────────────────────
  function handleFavClick(e) {
    const btn = e.target.closest('.fav-btn');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    // Sofort alle Mobile-Overlays schliessen (bevor async Toggle startet)
    document.querySelectorAll('.shelf-card--tapped').forEach(c => c.classList.remove('shelf-card--tapped'));

    const productId = btn.dataset.productId;
    if (!productId) return;

    // Nicht eingeloggt? → Login-Popup
    if (!isLoggedIn) {
      showLoginPrompt();
      return;
    }

    // Debounce: nicht doppelt klicken
    if (isLoading) return;

    toggleFavorite(productId, btn);
  }

  // ─── Toggle Favorit ──────────────────────────────────────────
  async function toggleFavorite(productId, btn) {
    isLoading = true;
    const wasActive = favoriteIds.has(productId);

    // Optimistic UI: sofort umschalten
    if (wasActive) {
      favoriteIds.delete(productId);
    } else {
      favoriteIds.add(productId);
    }
    updateAllButtons();
    updateHeaderCounter(!wasActive);
    animateButton(btn, !wasActive);

    try {
      const response = await fetch(TOGGLE_URL, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: productId }),
      });

      const data = await response.json();

      if (!data.success) {
        // Rollback bei Fehler
        if (wasActive) {
          favoriteIds.add(productId);
        } else {
          favoriteIds.delete(productId);
        }
        updateAllButtons();
        updateHeaderCounter();
        console.error('Favorites toggle error:', data, 'productId:', productId);
      } else {
        // Erfolg → Karte entfernen falls nötig (kein Toast, Herz-Animation reicht)
        if (wasActive) {
          removeFavoritesCard(productId);
        }
        // Event fuer Live-Updates (z.B. Favoriten-Seite)
        document.dispatchEvent(new CustomEvent('favorites:changed', {
          detail: { productId, action: wasActive ? 'removed' : 'added', count: favoriteIds.size }
        }));
      }
    } catch (error) {
      // Rollback bei Netzwerkfehler
      if (wasActive) {
        favoriteIds.add(productId);
      } else {
        favoriteIds.delete(productId);
      }
      updateAllButtons();
      updateHeaderCounter();
      console.error('Favorites network error:', error);
    } finally {
      isLoading = false;
    }
  }

  // ─── Karte von der Favoriten-Seite entfernen (mit Animation) ──
  function removeFavoritesCard(productId) {
    // Sofort alle Tapped-Overlays schliessen, damit nachrückende Karte nicht grün erscheint
    document.querySelectorAll('.shelf-card--tapped').forEach(c => c.classList.remove('shelf-card--tapped'));

    // Alle passenden Karten finden (Desktop + Mobile Grid) und ALLE animieren
    const allBtns = document.querySelectorAll(
      `.fav-btn[data-product-id="${productId}"]`
    );
    if (!allBtns.length) return;

    const allCards = [];
    allBtns.forEach(btn => {
      const cardEl = btn.closest('.favorites-card') || btn.closest('.shelf-card');
      if (cardEl) {
        cardEl.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        cardEl.style.opacity = '0';
        cardEl.style.transform = 'scale(0.9)';
        allCards.push(cardEl);
      }
    });

    setTimeout(() => {
      allCards.forEach(c => c.remove());
      // Pruefen ob die Liste jetzt leer ist (beide Grids checken)
      const grid = document.getElementById('favorites-grid');
      const gridMobile = document.getElementById('favorites-grid-mobile');
      const activeGrid = grid && grid.offsetParent !== null ? grid : gridMobile;
      var remainingCards = activeGrid ? activeGrid.querySelectorAll('.shelf-card, .favorites-card') : [];
      if (activeGrid && remainingCards.length === 0) {
        // Leeren Zustand anzeigen
        const page = document.getElementById('favorites-page');
        if (page) {
          const countEl = page.querySelector('.favorites-page__count');
          if (countEl) countEl.remove();
          if (grid) grid.remove();
          if (gridMobile) gridMobile.remove();
          const emptyHtml = '<div class="favorites-empty">' +
            '<svg class="favorites-empty__icon" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1.5">' +
            '<path fill-rule="evenodd" d="M10 5.24 8.515 3.773a4.433 4.433 0 0 0-6.21 0 4.293 4.293 0 0 0 0 6.128L10 17.495l7.695-7.593a4.293 4.293 0 0 0 0-6.128 4.433 4.433 0 0 0-6.21 0zm.765-2.177c2.113-2.084 5.538-2.084 7.65 0a5.29 5.29 0 0 1 0 7.55l-7.695 7.593a1.03 1.03 0 0 1-1.44 0l-7.696-7.594a5.29 5.29 0 0 1 0-7.549C3.697.98 7.122.98 9.234 3.063l.766.755z"/>' +
            '</svg>' +
            '<h2 class="favorites-empty__title">Noch keine Favoriten</h2>' +
            '<p class="favorites-empty__text">Klicke auf das Herz bei einem Produkt, um es hier zu speichern.</p>' +
            '<a href="/collections" class="button favorites-empty__btn">Produkte entdecken</a>' +
          '</div>';
          page.insertAdjacentHTML('beforeend', emptyHtml);
        }
      } else {
        // Counter aktualisieren
        const countEl = document.querySelector('.favorites-page__count');
        if (countEl) {
          const remaining = activeGrid ? activeGrid.querySelectorAll('.shelf-card, .favorites-card').length : 0;
          countEl.textContent = remaining + (remaining === 1 ? ' Produkt' : ' Produkte') + ' in deiner Liste';
        }
      }
    }, 300);
  }

  // ─── Alle Buttons aktualisieren ──────────────────────────────
  function updateAllButtons() {
    document.querySelectorAll('.fav-btn').forEach(btn => {
      const productId = btn.dataset.productId;
      if (!productId) return;

      if (favoriteIds.has(productId)) {
        btn.classList.add('fav-btn--active');
        btn.setAttribute('aria-label', 'Aus Favoriten entfernen');
      } else {
        btn.classList.remove('fav-btn--active');
        btn.setAttribute('aria-label', 'Zu Favoriten hinzufuegen');
      }
    });
  }

  // ─── Header Counter aktualisieren ────────────────────────────
  function updateHeaderCounter(bump) {
    const counter = document.querySelector('.header__fav-count');
    if (!counter) return;

    const count = favoriteIds.size;
    counter.textContent = count > 0 ? count : '';
    counter.dataset.count = count;
    counter.style.display = count > 0 ? 'flex' : 'none';

    if (bump) {
      counter.classList.remove('header__fav-count--bump');
      // Force reflow
      void counter.offsetWidth;
      counter.classList.add('header__fav-count--bump');
    }
  }

  // ─── Button Animation ────────────────────────────────────────
  function animateButton(btn, added) {
    btn.classList.remove('fav-btn--animating');
    void btn.offsetWidth;
    btn.classList.add('fav-btn--animating');

    // Partikel-Effekt nur beim Hinzufuegen
    if (added) {
      spawnParticles(btn);
    }

    setTimeout(() => btn.classList.remove('fav-btn--animating'), 500);
  }

  // ─── Partikel-Burst ──────────────────────────────────────────
  function spawnParticles(btn) {
    const container = btn.querySelector('.fav-btn__particles') || createParticleContainer(btn);

    for (let i = 0; i < 6; i++) {
      const particle = document.createElement('span');
      particle.className = 'fav-btn__particle';

      const angle = (i / 6) * Math.PI * 2;
      const distance = 20 + Math.random() * 15;
      particle.style.setProperty('--px', `${Math.cos(angle) * distance}px`);
      particle.style.setProperty('--py', `${Math.sin(angle) * distance}px`);

      container.appendChild(particle);
      setTimeout(() => particle.remove(), 600);
    }
  }

  function createParticleContainer(btn) {
    const container = document.createElement('span');
    container.className = 'fav-btn__particles';
    btn.appendChild(container);
    return container;
  }

  // ─── Login-Popup ─────────────────────────────────────────────
  function showLoginPrompt() {
    // Bereits sichtbar? Nicht nochmal öffnen
    if (document.querySelector('.fav-login-prompt--visible')) return;

    let prompt = document.querySelector('.fav-login-prompt');
    if (!prompt) {
      prompt = document.createElement('div');
      prompt.className = 'fav-login-prompt';
      prompt.innerHTML =
        '<div class="fav-login-prompt__backdrop"></div>' +
        '<div class="fav-login-prompt__dialog" role="alertdialog" aria-label="Anmelden">' +
          '<button type="button" class="fav-login-prompt__close" aria-label="Schließen">&times;</button>' +
          '<p class="fav-login-prompt__text">Melde dich an, um<br>Favoriten zu speichern ♥</p>' +
          '<a href="/account/login" class="fav-login-prompt__btn">Anmelden</a>' +
        '</div>';

      // Schließen bei Klick auf Backdrop oder X-Button
      prompt.querySelector('.fav-login-prompt__backdrop').addEventListener('click', closeLoginPrompt);
      prompt.querySelector('.fav-login-prompt__close').addEventListener('click', closeLoginPrompt);
      // Events im Dialog nicht durchblubbern lassen (aber Backdrop bleibt klickbar)
      prompt.querySelector('.fav-login-prompt__dialog').addEventListener('click', function(e) { e.stopPropagation(); });
      prompt.querySelector('.fav-login-prompt__dialog').addEventListener('touchstart', function(e) { e.stopPropagation(); });
      document.body.appendChild(prompt);
    }

    // Sichtbar machen (nächster Frame für Transition)
    requestAnimationFrame(() => {
      prompt.classList.add('fav-login-prompt--visible');
    });
  }

  function closeLoginPrompt() {
    const prompt = document.querySelector('.fav-login-prompt');
    if (prompt) {
      prompt.classList.remove('fav-login-prompt--visible');
    }
  }

  // ─── Herz-Button HTML erzeugen ────────────────────────────────
  // Wird von Liquid oder JS aufgerufen um einen Button zu erstellen
  window.createFavButton = function (productId, extraClass) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `fav-btn ${extraClass || ''}`.trim();
    btn.dataset.productId = String(productId);
    btn.setAttribute('aria-label', 'Zu Favoriten hinzufuegen');

    btn.innerHTML = HEART_EMPTY_SVG + HEART_FILLED_SVG;

    // Sofort richtigen State setzen
    if (favoriteIds.has(String(productId))) {
      btn.classList.add('fav-btn--active');
      btn.setAttribute('aria-label', 'Aus Favoriten entfernen');
    }

    return btn;
  };

  // ─── Oeffentliche API ─────────────────────────────────────────
  window.Favorites = {
    getIds: () => [...favoriteIds],
    getCount: () => favoriteIds.size,
    isActive: (productId) => favoriteIds.has(String(productId)),
    createButton: window.createFavButton,
    refresh: updateAllButtons,
  };

  // ─── Start ────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
